import { useQueryClient } from "@tanstack/react-query";
import { ReactQueryKeys } from "@/constants/common";
import { ContractState } from "@/types/contract";
import { useParams, usePathname } from "next/navigation";

type ContractStateReturn = ContractState & {
  getContract: () => ContractState["contract"];
  refetchContract: () => Promise<void>;
};

export function useContractState(allowEmptyId: true): Partial<ContractStateReturn>;
export function useContractState(): ContractStateReturn;

export function useContractState(allowEmptyId?: true): ContractStateReturn | Partial<ContractStateReturn> {
  const params = useParams();
  const pathname = usePathname();
  const queryClient = useQueryClient();

  if (!params.id) {
    if (allowEmptyId) {
      return {} as Partial<ContractStateReturn>;
    }

    throw new Error("require contract/:id or request/:id");
  }

  const isRequest = pathname.includes("/request");
  const queryKey = ReactQueryKeys.getContract(String(params.id), isRequest);

  const queryData = queryClient.getQueryData<ContractState>(queryKey);

  if (!queryData) {
    throw new Error("no contract state found");
  }

  return {
    ...queryData,
    getContract: () => queryClient.getQueryData<ContractState>(queryKey)!.contract,
    refetchContract: () => queryClient.refetchQueries({ queryKey }),
  };
}
